/** Guest & pre-check-in — the guest's own profile (it affects every booking of the
 *  guest, and needs console_user_manage to edit), this booking's travellers and the lead's
 *  pre-check-in, one row per party member (0–30; the list scrolls inside the panel after
 *  eight), every participant's pre-check-in answers (read-only, `PrecheckAnswers`), the
 *  signed waivers (legal evidence, never edited) and the masked booking-management link.
 *
 *  The guest's email and phone are its contacts list (O8) — the person record's widget, a
 *  full-width row after the name, language and country: the stored list in view mode, the
 *  records core's contacts widget in edit mode (its rows, markers, questions and errors).
 *  "Add guest contact" sits in the panel header (its height is kept in both modes, so Edit
 *  moves nothing). The guest's own email / phone (the divergence rows), each stored
 *  contact's OTA and dates (`GET /users/<id>/contacts/`, with console_user_view) and the
 *  booking's value actions reach the widget through `ContactsProvider`; the row layout
 *  follows the panel's edit-mode width. */
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingRecordStage } from '@/api/bookingRecord';
import { ActionControl } from '@/features/records/actions/ActionControl';
import { recordKey } from '@/features/records/draft/changeSet';
import { useDraftField } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import {
  AddContactMenu,
  contactsLayout,
  ContactsProvider,
  ContactsView,
  type ContactsContextValue,
} from '@/features/records/fields/widgets/ContactsWidget';
import { CONTACTS_FIELD } from '@/features/records/fields/widgets/contactsModel';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import type { ViewRow, ViewSection } from '@/features/records/views/types';
import { useContactsMeta } from '@/features/users/record/contacts/useContactsMeta';
import { formatZoned } from '@/utils/zonedTime';

import { contactActions } from '../../overview/bookingLinks';

import type { BookingDisplayContext } from './displays';
import { MaskedLinkReveal } from './MaskedLinkReveal';
import { PrecheckAnswers } from './PrecheckAnswers';
import { StagePanel } from './StagePanel';

/** Party rows beyond this many scroll inside the panel. */
const VISIBLE_PARTY_ROWS = 8;

/** One row per party member: who, then their fields on the panel's own field grid
 *  (wrapping, never scrolling sideways — every field, the pre-check-in state included,
 *  stays in view, and the grid's pinned column count holds when the ledger docks). */
export function PartyRows({ rows }: { rows: ViewRow[] }) {
  const { fieldByKey } = useRecordEditor();
  const listRef = useRef<HTMLUListElement>(null);
  const [rowPx, setRowPx] = useState(0);
  const capped = rows.length > VISIBLE_PARTY_ROWS;
  // The rows share one structure, so the first one's height caps the list at eight.
  useLayoutEffect(() => {
    const first = listRef.current?.firstElementChild;
    if (!capped || !first) return undefined;
    const measure = () => setRowPx(first.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(first);
    return () => ro.disconnect();
  }, [capped]);

  if (rows.length === 0) return null;
  return (
    <Box sx={{ display: 'grid', gap: 0.5 }}>
      <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
        Party · {rows.length} {rows.length === 1 ? 'member' : 'members'}
        <Box component="span" sx={{ fontWeight: 400 }}>
          {' '}
          · each is its own booking; fields that follow the lead are edited above
        </Box>
      </Typography>
      <Box
        component="ul"
        ref={listRef}
        aria-label="Party members"
        data-party-rows
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          overflowY: 'auto',
          maxHeight: capped && rowPx > 0 ? VISIBLE_PARTY_ROWS * rowPx : undefined,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {rows.map((row) => (
          <Box
            component="li"
            key={`${row.entity}:${row.id}`}
            aria-label={row.label}
            data-party-member={row.id}
            sx={{ px: 1.25, pt: 0.75, '& + &': { borderTop: 1, borderColor: 'divider' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              <Link
                component={RouterLink}
                to={`/bookings/${row.id}`}
                underline="hover"
                variant="body2"
                sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
              >
                {row.label}
              </Link>
              <Typography variant="caption" color="text.secondary">
                Booking #{row.id}
              </Typography>
            </Box>
            <Box sx={FIELD_GRID_SX}>
              {row.fields.map((f) => {
                const field = fieldByKey.get(f.key);
                return (
                  <FieldSlot
                    key={`${f.entity}:${f.id}:${f.key}`}
                    fieldKey={f.key}
                    entity={f.entity}
                    id={f.id}
                    path={f.path}
                    {...(field?.kind === 'action'
                      ? { trailing: <ActionControl field={field} rec={`${f.entity}:${f.id}`} subject={row.label} /> }
                      : {})}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** The party's signatures, newest first. The signed text is legal evidence: it is not in
 *  the read model, so its reveal stays disabled until the console can fetch it. */
export function WaiverList({ c }: { c: BookingDisplayContext }) {
  const sigs = c.detail.waivers?.signatures ?? [];
  if (sigs.length === 0) return null;
  return (
    <Stack component="span" spacing={0.5} sx={{ width: '100%' }}>
      {sigs.map((s) => (
        <Box component="span" key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <DescriptionOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Box component="span" sx={{ fontWeight: 600 }}>
            {s.waiver?.name ?? 'Waiver'}
          </Box>
          <span>
            {[s.first_name, s.last_name].filter(Boolean).join(' ') || `Booking #${s.participant_id}`} ·{' '}
            {s.agreed ? 'signed' : 'declined'} {formatZoned(s.created_ts, c.tz)}
          </span>
          <Tooltip title="The signed text is not available in the console yet">
            <span>
              <Button size="small" disabled startIcon={<VisibilityOutlinedIcon />} sx={{ py: 0 }}>
                Signed text
              </Button>
            </span>
          </Tooltip>
        </Box>
      ))}
    </Stack>
  );
}

/** The booking-management link logs the guest in, so only its masked form is shown,
 *  with the audited Reveal. */
export function BookingMgmtLink({ c }: { c: BookingDisplayContext }) {
  const link = c.detail.bookingmgmt_link;
  if (!link?.available || !link.masked) return null;
  return <MaskedLinkReveal bookingId={c.detail.id} kind="bookingmgmt" masked={link.masked} />;
}

/** The guest's stored contacts list — the contacts field's view-mode display. */
function GuestContactsView({ userId }: { userId: number }) {
  const draft = useDraftField('user', userId, CONTACTS_FIELD);
  return <ContactsView rec={recordKey('user', userId)} value={draft.baseline} />;
}

/** What the contacts widget knows of the guest beside its value: the account's own email
 *  / phone (the booking carries no phone_verified), the stored contacts' OTA and dates,
 *  the booking's value actions, the row layout. */
function useGuestContacts(display: BookingDisplayContext, editWidth: number): ContactsContextValue {
  const { detail, editContext: ctx, links } = display;
  const guest = ctx.guest;
  // Without console_user_view the contacts are left out — and so is their list.
  const visible = Boolean(guest && !guest.omitted_fields.includes(CONTACTS_FIELD));
  const meta = useContactsMeta(guest?.user_id ?? 0, visible);
  const layout = contactsLayout(editWidth);
  return useMemo<ContactsContextValue>(
    () => ({
      account: detail.guest
        ? {
            email: detail.guest.email || null,
            phone: detail.guest.phone || null,
            email_verified: guest?.email_verified ?? null,
            phone_verified: null,
          }
        : null,
      ...(meta ? { meta } : {}),
      valueActions: (type, value) => contactActions(type, value, links),
      layout,
    }),
    [detail.guest, guest?.email_verified, meta, links, layout],
  );
}

export function GuestPanel({
  section,
  display,
  editWidth = 0,
}: {
  section: ViewSection<BookingRecordStage>;
  display: BookingDisplayContext;
  editWidth?: number;
}) {
  const ctx = display.editContext;
  const guest = ctx.guest;
  const affects = guest?.affects_bookings ?? 0;
  const rows = section.blocks.find((b) => b.kind === 'editable')?.rows ?? [];
  const { mode, config, fieldByKey } = useRecordEditor();
  const contacts = useGuestContacts(display, editWidth);
  const hasContacts = section.blocks.some((b) => b.fields.some((f) => f.key === CONTACTS_FIELD));
  const contactsField = fieldByKey.get(CONTACTS_FIELD);
  const canAdd =
    mode === 'edit' &&
    guest !== null &&
    contactsField !== undefined &&
    (config.editabilityOf?.(recordKey('user', guest.user_id), contactsField).editable ?? true);
  return (
    <ContactsProvider value={contacts}>
      <StagePanel
        section={section}
        display={display}
        {...(hasContacts
          ? {
              meta: (
                // The header keeps the button's height in both modes: Edit moves nothing.
                <Box sx={{ minHeight: 30, display: 'flex', alignItems: 'center' }}>
                  {canAdd && guest && <AddContactMenu entity="user" id={guest.user_id} path={CONTACTS_FIELD} label="Add guest contact" />}
                </Box>
              ),
            }
          : {})}
        displays={{
          waivers: <WaiverList c={display} />,
          bookingmgmt_link: <BookingMgmtLink c={display} />,
          ...(guest ? { [CONTACTS_FIELD]: <GuestContactsView userId={guest.user_id} /> } : {}),
        }}
        groupEditable={(fields) => [
          {
            id: 'guest',
            title: (
              <>
                Guest
                {affects > 1 && (
                  <Box component="span" sx={{ fontWeight: 400 }}>
                    {' '}
                    · affects all {affects} bookings of this guest
                  </Box>
                )}
              </>
            ),
            fields: fields.filter((f) => f.entity === 'user'),
          },
          { id: 'booking', title: 'This booking', fields: fields.filter((f) => f.entity !== 'user') },
        ]}
        afterEditable={
          <>
            <PartyRows rows={rows} />
            <PrecheckAnswers summary={display.detail.precheckin} bookingId={ctx.booking_id} />
          </>
        }
      />
    </ContactsProvider>
  );
}
